/**
 * Staff AI Lead Intake HITL — paste/upload → review → Save / Accept / Edit.
 * Accept creates Lead via backend; Save only upserts IntakeDraft.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Drawer,
  Grid,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  MenuItem,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  acceptIntakeDraft,
  discardIntakeDraft,
  discardOpenIntakeDrafts,
  getIntakeDraft,
  ingestIntakeDraft,
  listIntakeDrafts,
  matchIntakeCustomer,
  saveIntakeDraft,
  uploadIntakeAttachments,
} from "../services/aiLeadIntakeService.js";
import {
  LOAD_TYPE_OPTIONS,
  buildHitlSaveFields,
  resolveItemsEditorText,
  resolveLoadTypeValue,
} from "./hitlFieldHelpers.js";
import LeadElementChecklist from "./LeadElementChecklist.jsx";
import { getLeadIntakeElementManifest } from "./leadIntakeElementsService.js";
import {
  buildElementChecklistRows,
  seedUnknownConfirmations,
} from "./elementChecklistHelpers.js";
import FieldConfidenceBadge from "./FieldConfidenceBadge.jsx";
import CustomerMatchBanner from "./CustomerMatchBanner.jsx";
import IntakeStepRail from "./IntakeStepRail.jsx";
import LeadSummaryPanel from "./LeadSummaryPanel.jsx";
import StructuredInventoryEditor from "./StructuredInventoryEditor.jsx";
import {
  applyCustomerMatchResolution,
  captureAiContactSnapshot,
} from "./customerMatchHelpers.js";
import { buildAcceptRequestPayload } from "./acceptPayloadHelpers.js";
import {
  buildConfirmationChecklistItems,
  deriveFieldConfidenceMap,
  evaluateConfirmationGate,
  getDesktopWizardSteps,
  getMobileWizardSteps,
} from "./fieldConfidenceHelpers.js";
import { getSetting } from "../../systemSettings/services/systemSettingsService.js";
import {
  hasInventoryContent,
  parseInventoryRowsFromFields,
  serializeInventoryRowsToItemsText,
} from "./structuredInventoryHelpers.js";

const SOURCE_OPTIONS = [
  { value: "paste", label: "Paste" },
  { value: "email_forward", label: "Email forward" },
  { value: "whatsapp_forward", label: "WhatsApp forward" },
  { value: "upload", label: "Upload / notes" },
];

const INTAKE_FILE_ACCEPT =
  "image/jpeg,image/png,image/gif,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/zip,application/x-zip-compressed,.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.webp,.zip";

function readinessColor(readiness) {
  switch (readiness) {
    case "ready":
      return "success";
    case "pending":
      return "warning";
    case "failed":
      return "error";
    default:
      return "default";
  }
}

function emptyFields() {
  return {
    name: "",
    phone: "",
    email: "",
    address: "",
    moving_from: "",
    moving_to: "",
    move_date: "",
    serviceType: "",
    distance_km: "",
    itemsText: "",
    inventoryRows: [],
    notes: "",
    elementConfirmations: [],
    qualification: null,
  };
}

function newBatchSlot() {
  return {
    id:
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `slot-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    rawText: "",
    files: [],
  };
}

const AiLeadIntakeView = () => {
  const navigate = useNavigate();
  const { draftId: routeDraftId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [rawText, setRawText] = useState("");
  const [sourceType, setSourceType] = useState("paste");
  const [runAutofill, setRunAutofill] = useState(true);
  const [awaitProviderPricing, setAwaitProviderPricing] = useState(true);
  const [attachmentUrls, setAttachmentUrls] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [batchMode, setBatchMode] = useState(false);
  const [batchSlots, setBatchSlots] = useState([newBatchSlot()]);
  const [draft, setDraft] = useState(null);
  const [fields, setFields] = useState(emptyFields());
  const [drafts, setDrafts] = useState([]);
  const [draftsDrawerOpen, setDraftsDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [manifestElements, setManifestElements] = useState([]);
  const [manifestCard, setManifestCard] = useState(null);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [manifestError, setManifestError] = useState(null);
  const [wizardStep, setWizardStep] = useState("customer_route");
  const [sourceDrawerOpen, setSourceDrawerOpen] = useState(false);
  const [summaryDrawerOpen, setSummaryDrawerOpen] = useState(false);
  const [confirmedFields, setConfirmedFields] = useState([]);
  const [customerMatch, setCustomerMatch] = useState(null);
  const [customerMatchLoading, setCustomerMatchLoading] = useState(false);
  const [aiContactSnapshot, setAiContactSnapshot] = useState(null);
  const [forceConfirmOverride, setForceConfirmOverride] = useState(false);
  const [attachmentsOverrideAvailable, setAttachmentsOverrideAvailable] =
    useState(false);
  const [minConfidenceToHighlight, setMinConfidenceToHighlight] = useState(0.5);
  const matchRequestRef = useRef(0);
  const draftIdRef = useRef(null);
  const suppressSourceOpenRef = useRef(false);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const result = await listIntakeDrafts({ status: "draft", limit: 30 });
      setDrafts(result.items || []);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to list drafts");
    } finally {
      setListLoading(false);
    }
  }, []);

  const applyDraft = useCallback((nextDraft, formPrefill, options = {}) => {
    const {
      resetConfirmations = true,
      openSource = true,
      resetWizardStep = true,
    } = options;
    const nextId = nextDraft?.id || null;
    const draftChanged = draftIdRef.current !== nextId;
    draftIdRef.current = nextId;

    setDraft(nextDraft);
    setCustomerMatch(nextDraft?.customerMatch || null);
    // Overrides must never survive Save / soft apply — only field confirms may.
    setForceConfirmOverride(false);
    setAttachmentsOverrideAvailable(false);
    if (resetConfirmations || draftChanged) {
      setConfirmedFields([]);
    }
    const f = nextDraft?.fields || {};
    const nextFields = {
      name: formPrefill?.customer?.name || f.name || "",
      phone: formPrefill?.phone || f.phone || "",
      email: formPrefill?.customer?.email || f.email || "",
      address: formPrefill?.customer?.address || f.address || "",
      moving_from: formPrefill?.moving_from || f.moving_from || "",
      moving_to: formPrefill?.moving_to || f.moving_to || "",
      move_date:
        formPrefill?.move_date ||
        (Array.isArray(f.move_dates) ? f.move_dates[0] : "") ||
        f.move_date ||
        "",
      serviceType: resolveLoadTypeValue(formPrefill?.serviceType, f),
      distance_km:
        formPrefill?.distance_km ||
        f.distance_km ||
        (f.qualification?.distance?.km != null
          ? String(f.qualification.distance.km)
          : "") ||
        "",
      itemsText: (() => {
        const rows = parseInventoryRowsFromFields({
          inventoryRows: f.inventoryRows,
          items: formPrefill?.items ?? f.items,
          itemsText: f.itemsText || formPrefill?.items,
        });
        return (
          serializeInventoryRowsToItemsText(rows) ||
          resolveItemsEditorText({
            items: formPrefill?.items,
            itemsText: f.itemsText,
            itemsArray: f.items,
          })
        );
      })(),
      inventoryRows: parseInventoryRowsFromFields({
        inventoryRows: f.inventoryRows,
        items: formPrefill?.items ?? f.items,
        itemsText: f.itemsText || formPrefill?.items,
      }),
      notes: formPrefill?.notes || f.notes || "",
      elementConfirmations: Array.isArray(f.elementConfirmations)
        ? f.elementConfirmations
        : Array.isArray(f.qualification?.elementConfirmations)
          ? f.qualification.elementConfirmations
          : [],
      qualification: f.qualification || formPrefill?.qualification || null,
    };
    setFields(nextFields);
    setAiContactSnapshot(captureAiContactSnapshot(nextFields));
    if (resetWizardStep) {
      setWizardStep("customer_route");
    }
    if (openSource && !suppressSourceOpenRef.current) {
      setSourceDrawerOpen(true);
    }
    suppressSourceOpenRef.current = false;
  }, []);

  const loadDraft = useCallback(
    async (id) => {
      if (!id) return;
      setLoading(true);
      setError("");
      try {
        const result = await getIntakeDraft(id);
        applyDraft(result.draft, result.formPrefill);
      } catch (err) {
        setError(err?.response?.data?.error || err.message || "Failed to load draft");
      } finally {
        setLoading(false);
      }
    },
    [applyDraft]
  );

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    let cancelled = false;
    getSetting("leads.intake.minConfidenceToHighlight")
      .then((response) => {
        if (cancelled) return;
        const raw =
          response?.data?.value ?? response?.value ?? response?.data ?? 0.5;
        const num = Number(raw);
        setMinConfidenceToHighlight(Number.isFinite(num) ? num : 0.5);
      })
      .catch(() => {
        if (!cancelled) setMinConfidenceToHighlight(0.5);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (routeDraftId) {
      loadDraft(routeDraftId);
    }
  }, [routeDraftId, loadDraft]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setManifestLoading(true);
      setManifestError(null);
      try {
        const result = await getLeadIntakeElementManifest();
        if (cancelled) return;
        setManifestElements(result.elements || []);
        setManifestCard(result.card || null);
      } catch (err) {
        if (cancelled) return;
        setManifestError(
          err?.response?.data?.error ||
            err.message ||
            "Failed to load card elements",
        );
      } finally {
        if (!cancelled) setManifestLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!manifestElements.length) return;
    setFields((prev) => {
      const seeded = seedUnknownConfirmations(
        manifestElements,
        prev.elementConfirmations,
      );
      const same =
        seeded.length === (prev.elementConfirmations || []).length &&
        seeded.every((row, i) => {
          const cur = prev.elementConfirmations[i];
          return (
            cur &&
            cur.id === row.id &&
            cur.status === row.status &&
            cur.value === row.value
          );
        });
      if (same) return prev;
      return { ...prev, elementConfirmations: seeded };
    });
  }, [manifestElements, draft?.id]);

  const accessIncomplete = useMemo(() => {
    const { incompleteAccess } = buildElementChecklistRows({
      elements: manifestElements,
      confirmations: fields.elementConfirmations,
    });
    return incompleteAccess;
  }, [manifestElements, fields.elementConfirmations]);

  const fieldConfidence = useMemo(
    () =>
      deriveFieldConfidenceMap({
        fields,
        draft,
        confirmedFields,
        minConfidenceToHighlight,
      }),
    [fields, draft, confirmedFields, minConfidenceToHighlight]
  );

  const checklistItems = useMemo(
    () =>
      buildConfirmationChecklistItems({
        fields,
        draft: draft
          ? { ...draft, customerMatch: customerMatch || draft.customerMatch }
          : null,
        fieldConfidence,
        accessIncomplete,
        confirmedFields,
      }),
    [fields, draft, customerMatch, fieldConfidence, accessIncomplete, confirmedFields]
  );

  const confirmationGate = useMemo(
    () => evaluateConfirmationGate(checklistItems),
    [checklistItems]
  );

  const wizardSteps = useMemo(
    () => (isMobile ? getMobileWizardSteps() : getDesktopWizardSteps()),
    [isMobile]
  );

  const completedStepIds = useMemo(() => {
    const done = [];
    if (String(fields.name || "").trim() && String(fields.phone || "").trim()) {
      done.push("customer_route");
    }
    if (hasInventoryContent(fields.inventoryRows, fields.itemsText)) {
      done.push("inventory");
    }
    if (!accessIncomplete) done.push("access_services");
    if (
      !confirmationGate.hardBlocked &&
      (!confirmationGate.softBlocked || forceConfirmOverride)
    ) {
      done.push("review");
    }
    if (isMobile && (rawText.trim() || draft?.rawText)) done.push("source");
    return done;
  }, [
    fields,
    accessIncomplete,
    confirmationGate.hardBlocked,
    confirmationGate.softBlocked,
    forceConfirmOverride,
    isMobile,
    rawText,
    draft,
  ]);

  const runCustomerMatch = useCallback(
    async ({
      phone,
      email,
      persist = true,
      clear = false,
      resolution,
      selectCustomerId,
    } = {}) => {
      if (!draft?.id || draft.status === "accepted") return;
      const reqId = ++matchRequestRef.current;
      setCustomerMatchLoading(true);
      try {
        const result = await matchIntakeCustomer(draft.id, {
          phone: phone ?? fields.phone,
          email: email ?? fields.email,
          persist,
          clear,
          resolution,
          selectCustomerId,
          // use_ai: keep customerId linked; do not preload CRM onto fields
          preloadFields: resolution !== "use_ai",
          fields: buildHitlSaveFields(fields),
        });
        if (reqId !== matchRequestRef.current) return;
        setCustomerMatch(result.customerMatch || null);
        if (result.draft) {
          setDraft(result.draft);
        }
        if (result.preloadedFields && Object.keys(result.preloadedFields).length) {
          setFields((prev) => ({ ...prev, ...result.preloadedFields }));
        }
        if (clear) {
          setAiContactSnapshot(captureAiContactSnapshot(fields));
        }
      } catch (err) {
        if (reqId !== matchRequestRef.current) return;
        // Non-fatal — ops can continue without match
        console.warn("Customer match failed", err);
      } finally {
        if (reqId === matchRequestRef.current) {
          setCustomerMatchLoading(false);
        }
      }
    },
    [draft?.id, draft?.status, fields]
  );

  // Match when draft loads / after autofill, and on phone/email edit (debounced).
  useEffect(() => {
    if (!draft?.id || draft.status === "accepted") return;
    const phone = String(fields.phone || "").trim();
    const email = String(fields.email || "").trim();
    if (!phone && !email) return undefined;
    const timer = setTimeout(() => {
      runCustomerMatch({ phone, email });
    }, 450);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce phone/email only
  }, [draft?.id, draft?.status, fields.phone, fields.email]);

  const handleUseCustomerFields = async () => {
    const snap = customerMatch?.customerSnapshot;
    setFields((prev) =>
      applyCustomerMatchResolution(prev, snap, "use_customer", aiContactSnapshot)
    );
    await runCustomerMatch({ resolution: "use_customer" });
  };

  const handleUseAiFields = async () => {
    // Keep customerId linked; Lead uses AI contact values; CRM is not written.
    setFields((prev) =>
      applyCustomerMatchResolution(
        prev,
        customerMatch?.customerSnapshot,
        "use_ai",
        aiContactSnapshot
      )
    );
    await runCustomerMatch({
      resolution: "use_ai",
    });
  };

  const handleSelectAmbiguousCustomer = async (customerId) => {
    await runCustomerMatch({ selectCustomerId: customerId });
  };

  const handleClearCustomerMatch = async () => {
    await runCustomerMatch({ clear: true });
  };

  const confirmField = (field) => {
    if (!field) return;
    setConfirmedFields((prev) =>
      prev.includes(field) ? prev : [...prev, field]
    );
  };

  const goNextStep = () => {
    const ids = wizardSteps.map((s) => s.id);
    const idx = ids.indexOf(wizardStep);
    if (idx >= 0 && idx < ids.length - 1) {
      const next = ids[idx + 1];
      setWizardStep(next);
      if (next !== "source") setSourceDrawerOpen(false);
    }
  };

  useEffect(() => {
    if (draft && wizardStep === "source") {
      setSourceDrawerOpen(true);
    }
  }, [draft, wizardStep]);

  const attachmentPreview = useMemo(() => {
    return (draft?.attachments || []).map((a) => (
      <Chip
        key={a.id || a.url || a.filename}
        size="small"
        label={`${a.filename || a.url || "file"} (${a.status})`}
        color={
          a.status === "ready"
            ? "success"
            : a.status === "failed"
              ? "error"
              : "warning"
        }
        component={a.url ? "a" : "div"}
        href={a.url || undefined}
        target={a.url ? "_blank" : undefined}
        clickable={Boolean(a.url)}
        sx={{ mr: 0.5, mb: 0.5 }}
      />
    ));
  }, [draft]);

  const uploadFilesToDraft = async (draftId, files) => {
    if (!draftId || !files?.length) return null;
    return uploadIntakeAttachments(draftId, files);
  };

  const handleIngest = async () => {
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const urlAttachments = attachmentUrls
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .map((url) => ({
          url,
          filename: url.split("/").pop() || "attachment",
          status: "ready",
        }));

      const hasText = Boolean(rawText.trim());
      const hasFiles = pendingFiles.length > 0;
      if (!hasText && !hasFiles && urlAttachments.length === 0) {
        setError(
          "Paste chat text and/or attach PDF, Word, images, or WhatsApp ZIP."
        );
        setLoading(false);
        return;
      }

      const idempotencyKey =
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `intake-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

      const filesToUpload = [...pendingFiles];
      const ingestText =
        rawText.trim() ||
        (hasFiles
          ? `Staff intake with ${filesToUpload.length} attached file(s): ${filesToUpload
              .map((f) => f.name)
              .join(", ")}`
          : "");

      const result = await ingestIntakeDraft({
        rawText: ingestText,
        sourceType: hasFiles && !hasText ? "upload" : sourceType,
        runAutofill,
        awaitProviderPricing,
        attachments: urlAttachments,
        idempotencyKey,
      });

      let nextDraft = result.draft;
      let formPrefill = result.formPrefill;
      if (filesToUpload.length > 0 && nextDraft?.id) {
        const uploadResult = await uploadFilesToDraft(
          nextDraft.id,
          filesToUpload
        );
        if (uploadResult?.draft) {
          nextDraft = uploadResult.draft;
          formPrefill = uploadResult.formPrefill || formPrefill;
        }
        if (uploadResult?.failed?.length) {
          setError(
            `Some uploads failed: ${uploadResult.failed
              .map((f) => f.filename || f.error)
              .join(", ")}`
          );
        }
        setPendingFiles([]);
      }

      applyDraft(nextDraft, formPrefill);
      setMessage(
        result.idempotent
          ? "Existing draft returned (idempotent)."
          : `Draft created${
              nextDraft?.attachments?.length ? " with attachments" : ""
            }. Review fields, then Save or Accept.`
      );
      navigate(`/leads/intake/${nextDraft.id}`, { replace: true });
      await loadList();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Ingest failed");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadMoreToDraft = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!draft?.id || files.length === 0) return;
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const uploadResult = await uploadFilesToDraft(draft.id, files);
      if (uploadResult?.draft) {
        applyDraft(uploadResult.draft, uploadResult.formPrefill);
      }
      const ready = uploadResult?.uploaded?.length || 0;
      const failed = uploadResult?.failed?.length || 0;
      setMessage(
        `Uploaded ${ready} file(s)${failed ? `, ${failed} failed` : ""}.`
      );
      await loadList();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const handleBatchSubmit = async () => {
    const slots = batchSlots.filter(
      (slot) =>
        String(slot.rawText || "").trim() ||
        (Array.isArray(slot.files) && slot.files.length > 0)
    );
    if (slots.length === 0) {
      setError(
        "Add at least one lead with chat text and/or attachments (PDF, Word, images, WhatsApp zip)."
      );
      return;
    }
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const results = [];
      let uploadFailures = 0;
      for (let i = 0; i < slots.length; i += 1) {
        const slot = slots[i];
        const text = String(slot.rawText || "").trim();
        const files = Array.isArray(slot.files) ? [...slot.files] : [];
        const ingestText =
          text ||
          (files.length
            ? `Staff batch intake with ${files.length} attached file(s): ${files
                .map((f) => f.name)
                .join(", ")}`
            : "");
        const idempotencyKey =
          typeof crypto !== "undefined" &&
          typeof crypto.randomUUID === "function"
            ? crypto.randomUUID()
            : `batch-${Date.now()}-${i}`;
        const created = await ingestIntakeDraft({
          rawText: ingestText,
          sourceType:
            files.length && !text
              ? "upload"
              : sourceType === "paste" && files.some((f) =>
                    String(f.name || "").toLowerCase().endsWith(".zip")
                  )
                ? "whatsapp_forward"
                : sourceType,
          runAutofill,
          awaitProviderPricing,
          idempotencyKey,
        });
        let draft = created.draft;
        if (files.length > 0 && draft?.id) {
          const uploadResult = await uploadFilesToDraft(draft.id, files);
          if (uploadResult?.draft) draft = uploadResult.draft;
          if (uploadResult?.failed?.length) {
            uploadFailures += uploadResult.failed.length;
          }
        }
        results.push({ success: Boolean(draft?.id), draft });
      }

      const createdCount = results.filter((r) => r.success).length;
      setMessage(
        `Batch ingest: ${createdCount} draft(s) created${
          uploadFailures ? `, ${uploadFailures} file upload issue(s)` : ""
        }. Review each draft, then Accept. One slot = one lead context.`
      );
      setBatchSlots([newBatchSlot()]);
      await loadList();
      const firstOk = results.find((r) => r.success && r.draft?.id);
      if (firstOk?.draft?.id) {
        // Prefer Open drafts list after batch — avoid fighting Source overlay.
        suppressSourceOpenRef.current = true;
        navigate(`/leads/intake/${firstOk.draft.id}`, { replace: true });
      }
      setSourceDrawerOpen(false);
      setDraftsDrawerOpen(true);
    } catch (err) {
      setError(
        err?.response?.data?.error || err.message || "Batch ingest failed"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDiscardDraft = async (draftId) => {
    if (!draftId) return;
    if (!window.confirm("Discard this draft? It will leave the open list.")) {
      return;
    }
    setLoading(true);
    setError("");
    setMessage("");
    try {
      await discardIntakeDraft(draftId);
      if (draft?.id === draftId) {
        setDraft(null);
        setFields(emptyFields());
        setSourceDrawerOpen(false);
        draftIdRef.current = null;
        setConfirmedFields([]);
        navigate("/leads/intake", { replace: true });
      }
      setMessage("Draft discarded.");
      await loadList();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Discard failed");
    } finally {
      setLoading(false);
    }
  };

  const handleClearOpenDrafts = async () => {
    if (drafts.length === 0) return;
    if (
      !window.confirm(
        `Discard all ${drafts.length} open draft(s)? Accepted leads are not affected.`
      )
    ) {
      return;
    }
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const result = await discardOpenIntakeDrafts();
      setDraft(null);
      setFields(emptyFields());
      setSourceDrawerOpen(false);
      draftIdRef.current = null;
      setConfirmedFields([]);
      setDraftsDrawerOpen(false);
      navigate("/leads/intake", { replace: true });
      setMessage(
        `Cleared ${result.discardedCount ?? drafts.length} open draft(s).`
      );
      await loadList();
    } catch (err) {
      setError(
        err?.response?.data?.error || err.message || "Clear open drafts failed"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!draft?.id) return;
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const result = await saveIntakeDraft(draft.id, {
        fields: buildHitlSaveFields(fields),
        customerId: draft.customerId || customerMatch?.customerSnapshot?.customerId || null,
        customerMatch: customerMatch || draft.customerMatch || null,
        note: "staff HITL save",
      });
      applyDraft(result.draft, result.formPrefill, {
        resetConfirmations: false,
        openSource: false,
        resetWizardStep: false,
      });
      setMessage("Draft saved (no Lead created).");
      await loadList();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async ({
    forceAttachmentOverride = false,
    forceConfirmOverride: confirmOverride = false,
  } = {}) => {
    if (!draft?.id) return;
    if (accessIncomplete) {
      setError(
        "Confirm Present or Absent for all Access card elements before Accept.",
      );
      return;
    }
    // Hard-blocks (errors) cannot be overridden — includes customer conflicts.
    if (confirmationGate.hardBlocked) {
      setError(
        "Resolve required items (name, phone, access, customer match) before Create Lead.",
      );
      setWizardStep("review");
      setSummaryDrawerOpen(true);
      return;
    }
    const allowConfirmSoft =
      Boolean(confirmOverride) || Boolean(forceConfirmOverride);
    if (confirmationGate.softBlocked && !allowConfirmSoft) {
      setError(
        `${checklistItems.length} item(s) still need confirmation. Review the checklist or use Create with confirmation override.`,
      );
      setWizardStep("review");
      setSummaryDrawerOpen(true);
      return;
    }
    setLoading(true);
    setError("");
    setMessage("");
    setAttachmentsOverrideAvailable(false);
    try {
      const saveFields = buildHitlSaveFields(fields);
      const linkedCustomerId =
        draft.customerId || customerMatch?.customerSnapshot?.customerId || null;
      // Persist latest field edits before Accept
      await saveIntakeDraft(draft.id, {
        fields: saveFields,
        customerId: linkedCustomerId,
        customerMatch: customerMatch || draft.customerMatch || null,
      });

      // forceAccept ONLY for attachment unreadiness — never for confirmation override
      const acceptPayload = buildAcceptRequestPayload({
        forceAttachmentOverride,
        forceConfirmOverride: allowConfirmSoft,
        awaitProviderPricing,
        fields: saveFields,
        customerId: linkedCustomerId,
      });
      const result = await acceptIntakeDraft(draft.id, acceptPayload);

      setMessage(
        result.idempotent
          ? `Already accepted as lead ${result.leadId}`
          : `Lead created ${result.leadId}${
              result.referenceNumber ? ` (${result.referenceNumber})` : ""
            }.${
              awaitProviderPricing
                ? " Marked Not priced — no auto draft quote. Export from Leads for providers."
                : " Pricing/draft quote pipeline will run."
            }`
      );
      setDraft(result.draft);
      if (result.leadId) {
        navigate(`/leads/${result.leadId}`);
      }
    } catch (err) {
      const code = err?.response?.data?.code;
      const msg = err?.response?.data?.error || err.message || "Accept failed";
      if (code === "ATTACHMENTS_NOT_READY") {
        setAttachmentsOverrideAvailable(true);
        setError(
          `${msg} Use “Override attachment unreadiness” if policy allows — confirmation override does not bypass this.`,
        );
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditInManualForm = () => {
    if (!draft) return;
    navigate("/leads/new", {
      state: {
        fromConversation: {
          phone: fields.phone,
          customerName: fields.name,
          notes: fields.notes,
          sourceType: draft.sourceType || "paste",
          formPrefill: {
            phone: fields.phone,
            customerName: fields.name,
            customer: {
              name: fields.name,
              phone: fields.phone,
              email: fields.email,
              address: fields.address,
            },
            notes: fields.notes,
            items: fields.itemsText,
            serviceType: fields.serviceType || "",
            distance_km: fields.distance_km || "",
            moving_from: fields.moving_from,
            moving_to: fields.moving_to,
            move_dates: fields.move_date ? [fields.move_date] : [""],
            intakeDraftId: draft.id,
          },
          missingFields: [],
        },
      },
    });
  };

  const openDraftFromDrawer = (draftId) => {
    setDraftsDrawerOpen(false);
    const alreadyOpen = String(draft?.id || "") === String(draftId || "");
    // Two left-anchor drawers conflict if opened in the same tick — open Source
    // after Open drafts starts closing. Same-route clicks skip loadDraft/applyDraft.
    window.setTimeout(() => {
      setSourceDrawerOpen(true);
    }, 75);
    if (!alreadyOpen) {
      navigate(`/leads/intake/${draftId}`);
    }
  };

  const updateField = (key) => (event) => {
    setFields((prev) => ({ ...prev, [key]: event.target.value }));
  };


  const sourcePanelBody = (
    <Stack spacing={2} data-testid="source-panel-body">

                {draft?.rawText ? (
                  <TextField
                    label="Original message"
                    value={draft.rawText}
                    multiline
                    minRows={4}
                    maxRows={12}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    helperText={`Source: ${draft.sourceType || "paste"}`}
                  />
                ) : null}
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Button
                    size="small"
                    variant={!batchMode ? "contained" : "outlined"}
                    onClick={() => setBatchMode(false)}
                  >
                    Single
                  </Button>
                  <Button
                    size="small"
                    variant={batchMode ? "contained" : "outlined"}
                    onClick={() => setBatchMode(true)}
                  >
                    Batch queue
                  </Button>
                </Stack>
                <TextField
                  select
                  label="Source"
                  value={sourceType}
                  onChange={(e) => setSourceType(e.target.value)}
                  fullWidth
                  size="small"
                >
                  {SOURCE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
                {batchMode ? (
                  <>
                    {batchSlots.map((slot, index) => (
                      <Box key={slot.id}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mb: 0.5 }}
                        >
                          <Typography variant="subtitle2">
                            Lead {index + 1}
                          </Typography>
                          {batchSlots.length > 1 ? (
                            <Button
                              size="small"
                              color="inherit"
                              onClick={() =>
                                setBatchSlots((prev) =>
                                  prev.filter((s) => s.id !== slot.id)
                                )
                              }
                            >
                              Remove
                            </Button>
                          ) : null}
                        </Stack>
                        <TextField
                          label={`Chat / paste #${index + 1}`}
                          value={slot.rawText}
                          onChange={(e) =>
                            setBatchSlots((prev) =>
                              prev.map((s) =>
                                s.id === slot.id
                                  ? { ...s, rawText: e.target.value }
                                  : s
                              )
                            )
                          }
                          multiline
                          minRows={5}
                          fullWidth
                        />
                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            component="label"
                            disabled={loading}
                          >
                            Attach files (PDF, Word, images, WhatsApp ZIP)
                            <input
                              hidden
                              type="file"
                              multiple
                              accept={INTAKE_FILE_ACCEPT}
                              onChange={(e) => {
                                const files = Array.from(e.target.files || []);
                                e.target.value = "";
                                if (!files.length) return;
                                setBatchSlots((prev) =>
                                  prev.map((s) =>
                                    s.id === slot.id
                                      ? {
                                          ...s,
                                          files: [
                                            ...(Array.isArray(s.files)
                                              ? s.files
                                              : []),
                                            ...files,
                                          ],
                                        }
                                      : s
                                  )
                                );
                              }}
                            />
                          </Button>
                          {Array.isArray(slot.files) && slot.files.length > 0 ? (
                            <Stack spacing={0.5}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Slot attachments ({slot.files.length}):
                              </Typography>
                              {slot.files.map((file, idx) => (
                                <Stack
                                  key={`${slot.id}-${file.name}-${idx}`}
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Typography
                                    variant="body2"
                                    noWrap
                                    sx={{ maxWidth: "80%" }}
                                  >
                                    {file.name}
                                  </Typography>
                                  <Button
                                    size="small"
                                    onClick={() =>
                                      setBatchSlots((prev) =>
                                        prev.map((s) =>
                                          s.id === slot.id
                                            ? {
                                                ...s,
                                                files: (s.files || []).filter(
                                                  (_, i) => i !== idx
                                                ),
                                              }
                                            : s
                                        )
                                      )
                                    }
                                  >
                                    Remove
                                  </Button>
                                </Stack>
                              ))}
                            </Stack>
                          ) : null}
                        </Stack>
                      </Box>
                    ))}
                    <Button
                      variant="outlined"
                      onClick={() =>
                        setBatchSlots((prev) => [...prev, newBatchSlot()])
                      }
                    >
                      Add another lead
                    </Button>
                  </>
                ) : (
                  <>
                    <TextField
                      label="Raw email / WhatsApp text"
                      value={rawText}
                      onChange={(e) => setRawText(e.target.value)}
                      multiline
                      minRows={8}
                      fullWidth
                    />
                    <Button variant="outlined" component="label" disabled={loading}>
                      Attach files (PDF, Word, images, WhatsApp ZIP)
                      <input
                        hidden
                        type="file"
                        multiple
                        accept={INTAKE_FILE_ACCEPT}
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          e.target.value = "";
                          if (files.length) {
                            setPendingFiles((prev) => [...prev, ...files]);
                          }
                        }}
                      />
                    </Button>
                    {pendingFiles.length > 0 ? (
                      <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          Pending upload ({pendingFiles.length}):
                        </Typography>
                        {pendingFiles.map((file, idx) => (
                          <Stack
                            key={`${file.name}-${idx}`}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography variant="body2" noWrap sx={{ maxWidth: "80%" }}>
                              {file.name}
                            </Typography>
                            <Button
                              size="small"
                              onClick={() =>
                                setPendingFiles((prev) =>
                                  prev.filter((_, i) => i !== idx)
                                )
                              }
                            >
                              Remove
                            </Button>
                          </Stack>
                        ))}
                      </Stack>
                    ) : null}
                    <TextField
                      label="Or attachment URLs (one per line, optional)"
                      value={attachmentUrls}
                      onChange={(e) => setAttachmentUrls(e.target.value)}
                      multiline
                      minRows={2}
                      fullWidth
                      helperText="Prefer Attach files above. URLs still work if already hosted."
                    />
                  </>
                )}
                <Button
                  variant={runAutofill ? "contained" : "outlined"}
                  onClick={() => setRunAutofill((v) => !v)}
                >
                  Autofill: {runAutofill ? "On" : "Off"}
                </Button>
                <Button
                  variant={awaitProviderPricing ? "contained" : "outlined"}
                  color={awaitProviderPricing ? "warning" : "inherit"}
                  onClick={() => setAwaitProviderPricing((v) => !v)}
                >
                  Provider pricing queue:{" "}
                  {awaitProviderPricing ? "On (no auto quote)" : "Off"}
                </Button>
                {batchMode ? (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={
                      loading ||
                      !batchSlots.some(
                        (s) =>
                          String(s.rawText || "").trim() ||
                          (Array.isArray(s.files) && s.files.length > 0)
                      )
                    }
                    onClick={handleBatchSubmit}
                  >
                    {loading ? (
                      <CircularProgress size={22} />
                    ) : (
                      "Submit all drafts"
                    )}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={
                      loading ||
                      (!rawText.trim() &&
                        pendingFiles.length === 0 &&
                        !attachmentUrls.trim())
                    }
                    onClick={handleIngest}
                  >
                    {loading ? <CircularProgress size={22} /> : "Create draft"}
                  </Button>
                )}
    </Stack>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: draft ? 1600 : 960, mx: "auto" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            AI Lead Intake
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and confirm information extracted from the customer&apos;s
            message. Edit where needed, then create the lead. Save keeps an
            IntakeDraft only — Accept creates Customer + Lead.
          </Typography>
          {draft ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <Chip size="small" label="Draft" />
              <Typography variant="caption" color="text.secondary">
                {completedStepIds.length} of {wizardSteps.length} steps ready
              </Typography>
              <LinearProgress
                variant="determinate"
                value={
                  wizardSteps.length
                    ? Math.min(
                        100,
                        (completedStepIds.length / wizardSteps.length) * 100
                      )
                    : 0
                }
                sx={{ width: 120, height: 6, borderRadius: 1 }}
              />
            </Stack>
          ) : null}
        </Box>
        <Button
          variant="outlined"
          onClick={() => setDraftsDrawerOpen(true)}
          disabled={listLoading}
          sx={{ flexShrink: 0, alignSelf: { xs: "stretch", sm: "flex-start" } }}
        >
          Open drafts ({drafts.length})
        </Button>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      {message ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage("")}>
          {message}
        </Alert>
      ) : null}

      <Drawer
        anchor={isMobile ? "bottom" : "left"}
        open={draftsDrawerOpen}
        onClose={() => setDraftsDrawerOpen(false)}
        PaperProps={{
          sx: isMobile
            ? {
                maxHeight: "75vh",
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                p: 2,
              }
            : { width: { xs: "100%", sm: 360 }, p: 2 },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">Open drafts</Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              color="warning"
              disabled={loading || listLoading || drafts.length === 0}
              onClick={handleClearOpenDrafts}
            >
              Clear all
            </Button>
            <Button size="small" onClick={() => setDraftsDrawerOpen(false)}>
              Close
            </Button>
          </Stack>
        </Stack>
        {listLoading ? (
          <CircularProgress size={22} />
        ) : drafts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No open drafts.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {drafts.map((d) => (
              <Stack
                key={d.id}
                direction="row"
                spacing={1}
                alignItems="stretch"
              >
                <Button
                  variant={draft?.id === d.id ? "contained" : "outlined"}
                  onClick={() => openDraftFromDrawer(d.id)}
                  sx={{
                    flex: 1,
                    justifyContent: "flex-start",
                    textTransform: "none",
                  }}
                >
                  <Box sx={{ textAlign: "left" }}>
                    <Typography variant="body2">
                      {d.fields?.name || d.fields?.phone || d.id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.sourceType} · {d.attachmentReadiness || "none"}
                      {Array.isArray(d.attachments) && d.attachments.length
                        ? ` · ${d.attachments.length} file(s)`
                        : ""}
                    </Typography>
                  </Box>
                </Button>
                <Button
                  size="small"
                  color="inherit"
                  disabled={loading}
                  onClick={() => handleDiscardDraft(d.id)}
                >
                  Discard
                </Button>
              </Stack>
            ))}
          </Stack>
        )}
      </Drawer>

      <Grid container spacing={2}>
        {!draft ? (
          <Grid item xs={12}>
            <Card data-testid="ingest-workspace">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Ingest
                </Typography>
                {sourcePanelBody}
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <>
          <Grid item xs={12} lg={9}>

          <Card data-testid="hitl-workspace">
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
                flexWrap="wrap"
                gap={1}
              >
                <Typography variant="h6">HITL review</Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSourceDrawerOpen(true)}
                      data-testid="open-source-drawer"
                    >
                      Source
                    </Button>
                    <Chip
                      size="small"
                      label={draft.status}
                      color={draft.status === "accepted" ? "success" : "default"}
                    />
                    <Chip
                      size="small"
                      label={`media: ${draft.attachmentReadiness || "none"}`}
                      color={readinessColor(draft.attachmentReadiness)}
                    />
                    {draft.confidence != null ? (
                      <Chip
                        size="small"
                        label={`AI ${(Number(draft.confidence) * 100).toFixed(0)}%`}
                      />
                    ) : null}
                    {checklistItems.length > 0 ? (
                      <Chip
                        size="small"
                        color="warning"
                        label={`${checklistItems.length} to confirm`}
                        onClick={() => setSummaryDrawerOpen(true)}
                      />
                    ) : (
                      <Chip size="small" color="success" label="Ready" />
                    )}
                    {isMobile ? (
                      <Button size="small" onClick={() => setSummaryDrawerOpen(true)}>
                        Summary
                      </Button>
                    ) : null}
                  </Stack>
              </Stack>

                <Stack spacing={2}>
                  <Typography variant="caption" color="text.secondary">
                    Draft ID: {draft.id}
                    {draft.autofill?.provider
                      ? ` · autofill: ${draft.autofill.provider}`
                      : ""}
                    {draft.customerId ? ` · customer: ${draft.customerId}` : ""}
                  </Typography>

                  <IntakeStepRail
                    steps={wizardSteps}
                    activeStepId={wizardStep}
                    completedStepIds={completedStepIds}
                    onStepChange={setWizardStep}
                  />

                  {(wizardStep === "customer_route" ||
                    wizardStep === "review") && (
                    <CustomerMatchBanner
                      match={customerMatch}
                      loading={customerMatchLoading}
                      onUseCustomer={handleUseCustomerFields}
                      onUseAi={handleUseAiFields}
                      onSelectCandidate={handleSelectAmbiguousCustomer}
                      onClear={handleClearCustomerMatch}
                    />
                  )}

                  {(wizardStep === "source" ||
                    wizardStep === "customer_route") && (
                    <>
                      {attachmentPreview}
                      {draft.status !== "accepted" ? (
                        <Button
                          size="small"
                          variant="outlined"
                          component="label"
                          disabled={loading}
                        >
                          Add files to this draft (PDF, Word, images, WhatsApp
                          ZIP)
                          <input
                            hidden
                            type="file"
                            multiple
                            accept={INTAKE_FILE_ACCEPT}
                            onChange={handleUploadMoreToDraft}
                          />
                        </Button>
                      ) : null}
                    </>
                  )}

                  {wizardStep === "source" ? (
                    <Alert severity="info" data-testid="step-source">
                      Source opens as an overlay drawer so HITL keeps full width.{" "}
                      <Button
                        size="small"
                        onClick={() => setSourceDrawerOpen(true)}
                      >
                        Open Source
                      </Button>
                    </Alert>
                  ) : null}

                  <Divider />

                  {wizardStep === "customer_route" ? (
                    <Box data-testid="step-customer-route">
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 1 }}
                      >
                        Customer &amp; Route
                      </Typography>
                      <Grid container spacing={2}>
                        {[
                          ["name", "Name"],
                          ["phone", "Phone"],
                          ["email", "Email"],
                          ["address", "Address"],
                          ["moving_from", "From"],
                          ["moving_to", "To"],
                          ["move_date", "Move date"],
                        ].map(([key, label]) => (
                          <Grid item xs={12} sm={6} key={key}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{ mb: 0.5 }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {label}
                              </Typography>
                              <FieldConfidenceBadge
                                level={fieldConfidence[key]?.level}
                                label={fieldConfidence[key]?.label}
                              />
                            </Stack>
                            <TextField
                              label={label}
                              value={fields[key] || ""}
                              onChange={updateField(key)}
                              fullWidth
                              size="small"
                              disabled={draft.status === "accepted"}
                            />
                          </Grid>
                        ))}
                        <Grid item xs={12} sm={6}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mb: 0.5 }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Load type
                            </Typography>
                            <FieldConfidenceBadge
                              level={fieldConfidence.serviceType?.level}
                              label={fieldConfidence.serviceType?.label}
                            />
                          </Stack>
                          <TextField
                            select
                            label="Load type"
                            value={fields.serviceType || ""}
                            onChange={updateField("serviceType")}
                            fullWidth
                            size="small"
                            disabled={draft.status === "accepted"}
                            helperText="Shared load uses corridor rates. Exclusive load is a dedicated vehicle."
                          >
                            <MenuItem value="">Select load type</MenuItem>
                            {LOAD_TYPE_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mb: 0.5 }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Route distance (km)
                            </Typography>
                            <FieldConfidenceBadge
                              level={fieldConfidence.distance_km?.level}
                              label={fieldConfidence.distance_km?.label}
                            />
                          </Stack>
                          <TextField
                            label="Route distance (km)"
                            value={fields.distance_km || ""}
                            onChange={updateField("distance_km")}
                            fullWidth
                            size="small"
                            disabled={draft.status === "accepted"}
                            inputProps={{ inputMode: "decimal" }}
                            helperText={
                              draft.autofill?.suggestions?.suggestedRouteKm !=
                                null && fields.distance_km
                                ? `Suggested: ${draft.autofill.suggestions.suggestedRouteKm} km — confirm or edit before Accept`
                                : "Optional. Leave blank if unknown."
                            }
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ) : null}

                  {wizardStep === "inventory" ? (
                    <Box data-testid="step-inventory">
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Inventory
                        </Typography>
                        <FieldConfidenceBadge
                          level={fieldConfidence.itemsText?.level}
                          label={fieldConfidence.itemsText?.label}
                        />
                      </Stack>
                      <StructuredInventoryEditor
                        rows={fields.inventoryRows || []}
                        disabled={draft.status === "accepted"}
                        onChange={(nextRows) =>
                          setFields((prev) => ({
                            ...prev,
                            inventoryRows: nextRows,
                            itemsText:
                              serializeInventoryRowsToItemsText(nextRows),
                          }))
                        }
                      />
                      <TextField
                        label="Notes"
                        value={fields.notes || ""}
                        onChange={updateField("notes")}
                        fullWidth
                        multiline
                        minRows={2}
                        disabled={draft.status === "accepted"}
                        sx={{ mt: 2 }}
                      />
                    </Box>
                  ) : null}

                  {wizardStep === "access_services" ? (
                    <Box data-testid="step-access-services">
                      <LeadElementChecklist
                        mode={
                          draft.status === "accepted" ? "readonly" : "edit"
                        }
                        elements={manifestElements}
                        confirmations={fields.elementConfirmations}
                        card={manifestCard}
                        loading={manifestLoading}
                        error={manifestError}
                        showAcceptGate={draft.status !== "accepted"}
                        onChange={(next) =>
                          setFields((prev) => ({
                            ...prev,
                            elementConfirmations: next,
                          }))
                        }
                      />
                    </Box>
                  ) : null}

                  {wizardStep === "review" ? (
                    <Box data-testid="step-review" sx={{ display: "grid", gap: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Review &amp; Create
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Confirm each section below, then create the lead. Edit
                        jumps back to that step — forms stay on their stage.
                      </Typography>

                      <Box
                        data-testid="review-summary-customer"
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          p: 1.5,
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mb: 1 }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700 }}
                          >
                            Customer &amp; Route
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => setWizardStep("customer_route")}
                          >
                            Edit
                          </Button>
                        </Stack>
                        <Typography variant="body2">
                          {[fields.name, fields.phone, fields.email]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {[fields.moving_from, fields.moving_to]
                            .filter(Boolean)
                            .join(" → ") || "Route not set"}
                          {fields.move_date ? ` · ${fields.move_date}` : ""}
                          {fields.serviceType
                            ? ` · ${fields.serviceType}`
                            : ""}
                          {fields.distance_km
                            ? ` · ${fields.distance_km} km`
                            : ""}
                        </Typography>
                      </Box>

                      <Box
                        data-testid="review-summary-inventory"
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          p: 1.5,
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mb: 1 }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700 }}
                          >
                            Inventory
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => setWizardStep("inventory")}
                          >
                            Edit
                          </Button>
                        </Stack>
                        <Typography variant="body2">
                          {(Array.isArray(fields.inventoryRows)
                            ? fields.inventoryRows.filter((r) =>
                                String(r?.name || "").trim()
                              ).length
                            : 0) ||
                            String(fields.itemsText || "")
                              .split(/[\n,]+/)
                              .filter((s) => s.trim()).length}{" "}
                          item(s)
                        </Typography>
                        {fields.notes ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            Notes: {fields.notes}
                          </Typography>
                        ) : null}
                      </Box>

                      <Box
                        data-testid="review-summary-access"
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          p: 1.5,
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mb: 1 }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700 }}
                          >
                            Access &amp; Services
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => setWizardStep("access_services")}
                          >
                            Edit
                          </Button>
                        </Stack>
                        <Typography variant="body2">
                          {accessIncomplete
                            ? "Access incomplete — Present/Absent required"
                            : "Access confirmed"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Full checklist lives on the Access step only.
                        </Typography>
                      </Box>

                      {confirmationGate.hardBlocked ? (
                        <Alert severity="error">
                          {
                            checklistItems.filter((i) => i.severity === "error")
                              .length
                          }{" "}
                          required item(s) block Create Lead (including
                          unresolved customer match). Resolve before accepting —
                          override is not allowed.
                        </Alert>
                      ) : confirmationGate.softBlocked &&
                        !forceConfirmOverride ? (
                        <Alert severity="warning">
                          {checklistItems.length} item(s) need confirmation
                          before Create Lead. Use Confirm in the summary, or
                          Create with confirmation override.
                          {draft.attachmentReadiness &&
                          draft.attachmentReadiness !== "none" &&
                          draft.attachmentReadiness !== "ready"
                            ? " Confirmation override does not bypass attachment unreadiness."
                            : ""}
                        </Alert>
                      ) : (
                        <Alert severity="success">
                          Ready to create lead from this IntakeDraft.
                        </Alert>
                      )}
                    </Box>
                  ) : null}

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      bgcolor: "background.paper",
                      py: 1,
                      zIndex: 2,
                      borderTop: 1,
                      borderColor: "divider",
                    }}
                  >
                    {wizardStep !== "review" ? (
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={loading || draft.status === "accepted"}
                        onClick={goNextStep}
                      >
                        Continue →
                      </Button>
                    ) : null}
                    <Button
                      variant="outlined"
                      disabled={loading || draft.status === "accepted"}
                      onClick={handleSave}
                    >
                      Save draft
                    </Button>
                    <Button
                      variant="outlined"
                      disabled={!draft}
                      onClick={handleEditInManualForm}
                    >
                      Edit in lead form
                    </Button>
                    {wizardStep === "review" ? (
                      <>
                        <Button
                          variant="contained"
                          color="success"
                          disabled={
                            loading ||
                            draft.status === "accepted" ||
                            accessIncomplete ||
                            Boolean(manifestError) ||
                            confirmationGate.hardBlocked ||
                            (confirmationGate.softBlocked &&
                              !forceConfirmOverride)
                          }
                          onClick={() => handleAccept({})}
                        >
                          Create Lead
                        </Button>
                        <Button
                          variant="text"
                          color="warning"
                          disabled={
                            loading ||
                            draft.status === "accepted" ||
                            accessIncomplete ||
                            Boolean(manifestError) ||
                            confirmationGate.hardBlocked ||
                            !confirmationGate.softBlocked
                          }
                          onClick={() => {
                            setForceConfirmOverride(true);
                            handleAccept({ forceConfirmOverride: true });
                          }}
                        >
                          Create with confirmation override
                        </Button>
                        {attachmentsOverrideAvailable ? (
                          <Button
                            variant="text"
                            color="warning"
                            disabled={
                              loading ||
                              draft.status === "accepted" ||
                              accessIncomplete ||
                              Boolean(manifestError) ||
                              confirmationGate.hardBlocked
                            }
                            onClick={() =>
                              handleAccept({
                                forceAttachmentOverride: true,
                                forceConfirmOverride: forceConfirmOverride,
                              })
                            }
                            data-testid="override-attachment-unreadiness"
                          >
                            Override attachment unreadiness
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                    <Button
                      variant="text"
                      color="error"
                      disabled={loading || draft.status === "accepted"}
                      onClick={() => handleDiscardDraft(draft.id)}
                    >
                      Discard
                    </Button>
                  </Stack>
                </Stack>
            </CardContent>
          </Card>
        </Grid>

          <Grid
            item
            xs={12}
            lg={3}
            sx={{ display: { xs: "none", lg: "block" } }}
          >
            <Box
              sx={{
                position: "sticky",
                top: 16,
              }}
            >
              <LeadSummaryPanel
                fields={fields}
                draft={draft}
                checklistItems={checklistItems}
                onConfirmField={confirmField}
                onNavigateChecklist={(item) => {
                  if (item.id === "access") setWizardStep("access_services");
                  else if (item.id === "inventory") setWizardStep("inventory");
                  else setWizardStep("customer_route");
                }}
              />
            </Box>
          </Grid>
          </>
        )}
      </Grid>

        <Drawer
          anchor={isMobile ? "bottom" : "left"}
          open={Boolean(draft) && sourceDrawerOpen}
          onClose={() => setSourceDrawerOpen(false)}
          PaperProps={{
            sx: isMobile
              ? {
                  maxHeight: "85vh",
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                  p: 2,
                }
              : { width: { xs: "100%", sm: 420 }, p: 2 },
          }}
          data-testid="source-drawer"
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">Source</Typography>
            <Button size="small" onClick={() => setSourceDrawerOpen(false)}>
              Close
            </Button>
          </Stack>
          {sourcePanelBody}
        </Drawer>

        <Drawer
          anchor="right"
          open={summaryDrawerOpen}
          onClose={() => setSummaryDrawerOpen(false)}
          PaperProps={{ sx: { width: { xs: "100%", sm: 380 }, p: 0 } }}
        >
          <LeadSummaryPanel
            fields={fields}
            draft={draft}
            checklistItems={checklistItems}
            onConfirmField={(field) => {
              confirmField(field);
              setSummaryDrawerOpen(false);
            }}
            onNavigateChecklist={(item) => {
              if (item.id === "access") setWizardStep("access_services");
              else if (item.id === "inventory") setWizardStep("inventory");
              else setWizardStep("customer_route");
              setSummaryDrawerOpen(false);
            }}
          />
          <Box sx={{ p: 2 }}>
            <Button fullWidth onClick={() => setSummaryDrawerOpen(false)}>
              Close
            </Button>
          </Box>
        </Drawer>
    </Box>
  );
};

export default AiLeadIntakeView;
